import {datadogRum} from "@datadog/browser-rum";
import {assign} from "lodash";


type InitializeDataDogRumParam = Readonly<{
    attributes: Record<string, string>;
}>;

export function initializeDataDogRum({attributes}: InitializeDataDogRumParam) {
    
    const config = datadogConfiguration();
    
    if (config !== 'NOPE') {
        datadogRum.init({
            applicationId: 'f3bb6610-c8a9-4daa-b669-c4f25f1ff541',
            clientToken: 'pub53c278d2aede788830b6dbc9d5f6cae7',
            // `site` refers to the Datadog site parameter of your organization
            // see https://docs.datadoghq.com/getting_started/site/
            site: 'datadoghq.com',
            service: config.service,
            env: config.env,
            version: config.version,
            sessionSampleRate: config.sessionSampleRate,
            sessionReplaySampleRate: config.sessionReplaySampleRate,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: 'allow', //'mask-user-input',
            beforeSend: event => {
                event.context ??= {};
                assign(event.context, attributes);
                return true;
            },
            allowedTracingUrls: [
                window.location.origin,
            ]
        });
    }
}

export function setAnalyticsUser(email: string): void {
    datadogRum.setUser({email});    
}
export function clearAnalyticsUser(): void {
    datadogRum.clearUser();
}

export function setAnalyticsProperty(key: string, value: string | number | boolean | null) {
    datadogRum.setGlobalContextProperty(key, value);
}
export function setAnalyticsViewProperty(key: string, value: string | number | boolean | null) {
    datadogRum.setViewContextProperty(key, value);
}

type DataDogConfiguration = Readonly<{ 
    env: string;
    service: string;
    sessionReplaySampleRate: number;
    sessionSampleRate: number;
    version: string;
} | 'NOPE'>

function datadogConfiguration(): DataDogConfiguration {
    const env = process.env.REACT_APP_DD_RUM_ENV;
    const sampleRate = Number(process.env.REACT_APP_DD_RUM_SAMPLE_RATE);
    const sessionReplaySampleRate = Number(process.env.REACT_APP_DD_RUM_REPLAY_SAMPLE_RATE);
    const version = process.env.REACT_APP_DD_RUM_VERSION;
    
    if (!env) return 'NOPE';

    let conditionalSampleRate = 100;
    if (isUserAgentBot()) {
        conditionalSampleRate = 0;
    } else if (!isNaN(sampleRate)) {
        conditionalSampleRate = sampleRate
    }

    return {
        env,
        version: version ?? 'unknown',
        service: process.env.REACT_APP_DD_RUM_SERVICE || 'web',
        sessionSampleRate: conditionalSampleRate,
        sessionReplaySampleRate: !isNaN(sessionReplaySampleRate) ? sessionReplaySampleRate : 100
    }
}

function isUserAgentBot(): boolean {
    const botPattern = "(googlebot|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)";
    const regex = new RegExp(botPattern, 'i');
    return regex.test(navigator.userAgent);
}

export const SECURE_ELEMENT_CLASS = "dd-privacy-mask"

/**
 * Structure of an analytics event.
 * @typeParam Attributes
 */
export interface AnalyticsEvent<Attributes extends object = {}> {
    /**
     * Gets the name for the event. The target will be provided but hopefully it can be ignored
     */
    getEventName(): string;

    /**
     * Gets the attributes for the specific target. Returning null will send no attributes to the target.
     * returning false will prevent sending the event to the target
     */
    getEventAttributes(): Attributes | null | false;
}

/**
 * Sends an analytic event. Currently only sends to datadog. Will not send if `event.getEventAttributes`
 * returns `false`
 */
export function sendEvent<
    Event extends AnalyticsEvent<Attributes>,
    Attributes extends object = {},
>(event: Event) {
    const eventName = event.getEventName();
    const eventAttributes = event.getEventAttributes();
    if (eventAttributes !== false) {
        datadogRum.addAction(eventName, eventAttributes ?? {});
    }
}